<template>
  <div class="upcoming-draws upcoming-draws-view">
    <LotterySection
      :title="$t('sections.upcomingDraws')"
      :lotteries="upcomingDrawsStore.draws"
      :isFavorited="isFavorited"
      :showFilters="true"
      @bet="handleBet"
      @favorite="handleFavorite"
      @removeLottery="removeLottery"
    />

    <FavoritesSection
      :favorites="favorites"
      :isFavorited="isFavorited"
      @favorite="handleFavorite"
    />
    <MostPlayedSection
      :mostPlayed="mostPlayed"
      :isFavorited="isFavorited"
      @favorite="handleFavorite"
    />

    <div class="lucky-numbers-section">
      <LuckyNumbersSection
        :title="$t('sections.luckyNumbers')"
        :games="upcomingDrawsStore.draws"
        :isFavorited="isFavorited"
        titleAlign="left"
        :dateFilterOnly="false"
        @bet="handleBet"
        @favorite="handleFavorite"
        @remove="removeLottery"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import LotterySection from '@/components/LotterySection.vue'
import FavoritesSection from "@/components/FavoritesSection.vue";
import MostPlayedSection from "@/components/MostPlayedSection.vue";
import LuckyNumbersSection from "@/components/LuckyNumbersSection.vue";
import { useUpcomingDrawsStore } from "@/stores/upcomingDraws";
import type { UpcomingDraw } from "@/types/lottery";
import { useRouter } from 'vue-router';
import { useAuthStore } from "@/stores/auth";

const authStore = useAuthStore()
const router = useRouter();
const favorites = ref<UpcomingDraw[]>([]);
const mostPlayed = ref<UpcomingDraw[]>([]);
const upcomingDrawsStore = useUpcomingDrawsStore();

// Load favorites from local storage
const loadFavorites = () => {
  const savedFavorites = localStorage.getItem('favoriteDraws');
  if (savedFavorites) {
    const cachedFavorites = JSON.parse(savedFavorites);
    favorites.value = syncWithFreshData(cachedFavorites);
  }
};

const syncWithFreshData = (cachedData: UpcomingDraw[]) => {
  return cachedData.map(cachedItem => {
    const freshData = upcomingDrawsStore.draws.find(draw => draw.game_slug === cachedItem.game_slug);
    if (freshData) {
      return {
        ...cachedItem,
        draw_date: freshData.draw_date,
        cutoff: freshData.cutoff,
        game_name: freshData.game_name,
        game_logo: freshData.game_logo,
      };
    }
    return cachedItem;
  });
};

// Save favorites to local storage
const saveFavorites = () => {
  localStorage.setItem('favoriteDraws', JSON.stringify(favorites.value));
};

// Load most played from local storage and sync with fresh data
const loadMostPlayed = () => {
  const savedMostPlayed = localStorage.getItem('mostPlayedDraws');
  if (savedMostPlayed) {
    const mostPlayedData = JSON.parse(savedMostPlayed);
    // Sync with fresh data and sort by play count, take top 5
    const syncedData = syncWithFreshData(mostPlayedData);
    mostPlayed.value = syncedData
      .sort((a: UpcomingDraw & { playCount: number }, b: UpcomingDraw & { playCount: number }) => b.playCount - a.playCount)
      .slice(0, 5);
  }
};

// Save most played to local storage
const saveMostPlayed = (draw: UpcomingDraw) => {
  const savedMostPlayed = localStorage.getItem('mostPlayedDraws');
  let mostPlayedData = savedMostPlayed ? JSON.parse(savedMostPlayed) : [];
  
  // Find if draw already exists in most played
  const existingIndex = mostPlayedData.findIndex((item: UpcomingDraw & { playCount: number }) => item.id === draw.id);
  
  if (existingIndex !== -1) {
    // Increment play count if draw exists
    mostPlayedData[existingIndex].playCount++;
  } else {
    // Add new draw with play count 1
    mostPlayedData.push({ ...draw, playCount: 1 });
  }
  
  // Sort by play count and take top 5
  mostPlayedData = mostPlayedData
    .sort((a: UpcomingDraw & { playCount: number }, b: UpcomingDraw & { playCount: number }) => b.playCount - a.playCount)
    .slice(0, 5);
  
  // Save to local storage
  localStorage.setItem('mostPlayedDraws', JSON.stringify(mostPlayedData));
  
  // Update mostPlayed ref
  mostPlayed.value = mostPlayedData;
};

const handleBet = (draw: UpcomingDraw) => {
  saveMostPlayed(draw); // Track this bet
  router.push(`/lottery/${draw.game_slug}`);
};

const handleFavorite = (draw: UpcomingDraw) => {
  if (!draw || typeof draw.id === 'undefined') return;
  const index = favorites.value.findIndex(fav => fav.id === draw.id);
  if (index === -1) {
    favorites.value.push(draw);
  } else {
    favorites.value.splice(index, 1);
  }
  saveFavorites();
};

const isFavorited = (draw: UpcomingDraw) => {
  if (!draw || typeof draw.id === 'undefined') return false;
  return favorites.value.some(fav => fav.id === draw.id);
};

const removeLottery = (lotteryToRemove: UpcomingDraw) => {
  const idx = upcomingDrawsStore.draws.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    upcomingDrawsStore.draws.splice(idx, 1);
    if (upcomingDrawsStore.draws.length === 0) {
      upcomingDrawsStore.fetchDraws(1);
    }
  }
};

watch(
  () => upcomingDrawsStore.draws,
  () => {
    if (upcomingDrawsStore.draws.length > 0) {
      loadFavorites();
      loadMostPlayed();
    }
  },
  { deep: true }
);

onMounted(async () => {
  // Initialize auth store from sessionStorage
  authStore.initializeFromSession();
  
  loadFavorites();
  loadMostPlayed();
  await upcomingDrawsStore.fetchDraws(1);
});
</script>

<style scoped>

.section {
  margin-bottom: 48px;
}

/* Custom styles specific to UpcomingDrawsView */

.lottery-container {
  position: relative;
  width: 100%;
  padding: 0 8px;
  margin: 24px 0;
  overflow: hidden;
}

.lottery-grid {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 16px 0;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
}

.lottery-grid > * {
  flex: 0 0 calc(20% - 11.2px);
  min-width: 0;
  scroll-snap-align: start;
}

@media (max-width: 768px) {
  .upcoming-draws-view {
    padding: 0 8px;
  }
  
  .lottery-grid {
    margin: 0;
    padding: 0;
    gap: 12px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    overflow-x: auto;
    scroll-snap-type: x mandatory;
  }

  .lottery-grid > * {
    flex: none;
    scroll-snap-align: start;
    width: 100%;
  }

  .lottery-container {
    padding: 0;
    margin: 16px 0;
  }
  
  .section {
    margin-bottom: 32px;
  }
  
  /* Lucky Numbers section specific mobile styling */
  .lucky-numbers-section .numbers-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
  }
  
  .lucky-numbers-section .numbers-grid > * {
    width: 100%;
    max-width: 100%;
  }
}

.scroll-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--bg-dark-tertiary);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-white);
  z-index: 10;
  transition: all 0.2s;
}

.scroll-btn:hover {
  background: var(--bg-tertiary);
}

.scroll-left {
  left: -12px;
}

.scroll-right {
  right: -12px;
}

/* Lucky Numbers section specific styles */
.numbers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  padding: 4px 0;
}

.numbers-grid > * {
  min-width: 0;
}

/* Left-align title for Upcoming Draws section only */
.upcoming-draws-view .lottery-section:first-child .section-title.medium {
  text-align: left !important;
}

/* Alternative selector for more specificity */
.upcoming-draws-view .section .section-header .section-title {
  text-align: left !important;
}

/* Most specific selector */
.upcoming-draws-view .section.lottery-section .section-header .section-title.medium {
  text-align: left !important;
}

/* Target LuckyNumbersSection in UpcomingDrawsView only */
.upcoming-draws-view .lucky-numbers-section .section-title {
  text-align: left !important;
}

/* More specific targeting for h2 element */
.upcoming-draws-view .lucky-numbers-section h2.section-title.medium {
  text-align: left !important;
}
</style>